import React, { useState, useEffect } from 'react';
import {
  Plus, Search, ImageIcon, FileText, ChevronRight, MapPin,
  List, LayoutGrid, Calendar, Clock, CheckCircle2
} from 'lucide-react';
import { getFileUrl } from '../../lib/pb';
import ChantierModal from '../../components/admin/ChantierModal';
import { useData } from '../../context/DataContext';
import '../../styles/admin.css';

const STATUS_LABELS = {
  'planned': 'Planifié',
  'in_progress': 'En Cours',
  'active': 'En Cours',
  'completed': 'Terminé',
  'archived': 'Archivé'
};

export default function AdminChantiers() {
  const { chantiers, loading: globalLoading, refresh } = useData();
  const [items, setItems] = useState([]);
  const [search, setSearch] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [viewMode, setViewMode] = useState('grid');

  useEffect(() => {
    if (!chantiers) return;
    const filtered = chantiers.filter(c =>
      !search ||
      c.title?.toLowerCase().includes(search.toLowerCase()) ||
      c.client_name?.toLowerCase().includes(search.toLowerCase()) ||
      c.location?.toLowerCase().includes(search.toLowerCase())
    );
    setItems(filtered);
  }, [search, chantiers]);

  return (
    <div className="chantiers-page">
      {/* HEADER */}
      <header className="chantiers-header">
        <div className="header-text">
          <h1>Suivi des Chantiers</h1>
          <p className="muted">Gérez vos réalisations, documents techniques et photos terrain.</p>
        </div>
        <button className="btn-add-main" onClick={() => { setEditing(null); setIsModalOpen(true); }}>
          <Plus size={20} />
          <span>Nouveau Chantier</span>
        </button>
      </header>

      {/* SEARCH & FILTERS */}
      <div className="chantiers-filters" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div className="search-box">
          <Search size={18} />
          <input type="text" placeholder="Rechercher par titre ou client..." value={search} onChange={e => setSearch(e.target.value)} />
        </div>

        <div className="view-switcher-premium" style={{ display: 'flex', background: '#f1f5f9', padding: 4, borderRadius: 12 }}>
          <button
            type="button"
            className={viewMode === 'list' ? 'active' : ''}
            onClick={() => setViewMode('list')}
            style={{
              border: 'none', background: viewMode === 'list' ? '#fff' : 'transparent',
              padding: '8px 12px', borderRadius: 8, cursor: 'pointer', display: 'flex', alignItems: 'center',
              color: viewMode === 'list' ? '#0ea5e9' : '#64748b', boxShadow: viewMode === 'list' ? '0 2px 4px rgba(0,0,0,0.05)' : 'none'
            }}
          >
            <List size={18} />
          </button>
          <button
            type="button"
            className={viewMode === 'grid' ? 'active' : ''}
            onClick={() => setViewMode('grid')}
            style={{
              border: 'none', background: viewMode === 'grid' ? '#fff' : 'transparent',
              padding: '8px 12px', borderRadius: 8, cursor: 'pointer', display: 'flex', alignItems: 'center',
              color: viewMode === 'grid' ? '#0ea5e9' : '#64748b', boxShadow: viewMode === 'grid' ? '0 2px 4px rgba(0,0,0,0.05)' : 'none'
            }}
          >
            <LayoutGrid size={18} />
          </button>
        </div>
      </div>

      {/* CONTENT AREA */}
      <main className="chantiers-main">
        {globalLoading ? (
          <div className="skeleton-grid">
            {[1, 2, 3, 4, 5, 6].map(i => (
              <div key={i} className="skeleton-card" style={{ height: 350, background: '#f8fafc', borderRadius: 28, animation: 'pulse 1.5s infinite' }}></div>
            ))}
          </div>
        ) : (
          <>
            {viewMode === 'list' ? (
              <div className="premium-list" style={{ display: 'grid', gap: 10 }}>
                {items.map(item => (
                  <div
                    key={item.id}
                    className="list-item-row"
                    onClick={() => { setEditing(item); setIsModalOpen(true); }}
                    style={{
                      background: '#fff', border: '1px solid #f1f5f9', borderLeft: `6px solid ${item.color || '#e2e8f0'}`,
                      padding: '12px 20px', borderRadius: 16, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 20
                    }}
                  >
                    <div style={{ width: 44, height: 44, borderRadius: 12, background: '#f8fafc', overflow: 'hidden', flexShrink: 0 }}>
                      {item.gallery?.length > 0 ? (
                        <img src={getFileUrl(item, item.gallery[0])} alt="Site" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                      ) : (
                        <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#cbd5e1' }}><ImageIcon size={20} /></div>
                      )}
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontWeight: 800, fontSize: 15, color: item.color || '#0f172a' }}>{item.title}</div>
                      <div style={{ fontSize: 12, fontWeight: 700, color: '#94a3b8' }}>{item.client_name || 'Client AEF 32'}</div>
                    </div>
                    <div style={{ flex: 1, minWidth: 0, display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, color: '#64748b', fontWeight: 600 }}>
                      <MapPin size={14} style={{ color: item.color || '#cbd5e1' }} />
                      <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{item.location || 'Localisation inconnue'}</span>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                      <span
                        style={{
                          fontSize: 10, fontWeight: 900, textTransform: 'uppercase', padding: '5px 12px', borderRadius: 8,
                          background: (item.status === 'planned' || item.status === 'active') ? (item.color + '15') : '#f1f5f9',
                          color: (item.status === 'planned' || item.status === 'active') ? (item.color || '#0ea5e9') : '#64748b',
                          minWidth: 80, textAlign: 'center'
                        }}
                      >
                        {STATUS_LABELS[item.status] || item.status}
                      </span>
                      <div style={{ display: 'flex', gap: 10, color: '#cbd5e1' }}>
                        {item.attachments?.length > 0 && <FileText size={16} />}
                        {item.gallery?.length > 0 && <ImageIcon size={16} />}
                      </div>
                    </div>
                    <ChevronRight size={18} style={{ color: '#cbd5e1' }} />
                  </div>
                ))}
              </div>
            ) : (
              <div className="chantiers-grid">
                {items.map(item => (
                  <div key={item.id} className="chantier-card" onClick={() => { setEditing(item); setIsModalOpen(true); }} style={{ borderLeft: `6px solid ${item.color || '#e2e8f0'}` }}>
                    <div className="card-thumb">
                      {item.gallery?.length > 0 ? (
                        <img src={getFileUrl(item, item.gallery[0])} alt="Site" />
                      ) : (
                        <div className="thumb-placeholder"><ImageIcon size={32} /></div>
                      )}
                      <span className={`status-badge ${item.status}`} style={{ background: item.status === 'planned' || item.status === 'active' ? item.color : undefined }}>{STATUS_LABELS[item.status] || item.status}</span>
                    </div>
                    <div className="card-body">
                      <div className="card-info">
                        <h3 style={{ color: item.color || '#0f172a' }}>{item.title}</h3>
                        <p className="card-client">{item.client_name || 'Client AEF 32'}</p>
                        <div className="card-loc"><MapPin size={12} style={{ color: item.color || '#64748b' }} /> {item.location || 'Localisation...'}</div>
                      </div>
                      <div className="card-footer">
                        <div className="media-stats">
                          {item.attachments?.length > 0 && <span title="Documents"><FileText size={14} /> {item.attachments.length}</span>}
                          {item.gallery?.length > 0 && <span title="Photos"><ImageIcon size={14} /> {item.gallery.length}</span>}
                        </div>
                        <ChevronRight size={18} className="arrow" style={{ color: item.color || undefined }} />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
            {items.length === 0 && <div className="empty-state">Aucun chantier trouvé pour votre recherche.</div>}
          </>
        )}
      </main>

      {isModalOpen && <ChantierModal item={editing} onClose={() => setIsModalOpen(false)} onSaved={refresh} />}

      <style>{`
        .skeleton-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(320px, 1fr)); gap: 20px; }
        @keyframes pulse { 0% { opacity: 0.6; } 50% { opacity: 1; } 100% { opacity: 0.6; } }

        .chantiers-page { display: flex; flex-direction: column; gap: 25px; animation: fadeIn 0.4s ease; }
        .chantiers-header { display: flex; justify-content: space-between; align-items: flex-end; }
        .chantiers-header h1 { font-size: 28px; font-weight: 900; margin: 0 0 5px 0; color: #0f172a; }
        .btn-add-main { background: var(--admin-primary); border: none; font-weight: 800; border-radius: 16px; padding: 12px 24px; display: flex; align-items: center; gap: 10px; cursor: pointer; box-shadow: 0 10px 15px -3px rgba(234, 179, 8, 0.2); }
        
        .chantiers-filters { background: #fff; border: 1px solid var(--admin-border); border-radius: 20px; padding: 15px; }
        .search-box { display: flex; align-items: center; gap: 10px; color: #94a3b8; }
        .search-box input { border: none; outline: none; width: 100%; font-size: 14px; font-weight: 600; color: #0f172a; }

        /* GRID & CARDS */
        .chantiers-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(320px, 1fr)); gap: 20px; }
        .chantier-card { background: #fff; border: 1px solid var(--admin-border); border-radius: 28px; overflow: hidden; cursor: pointer; transition: all 0.3s; position: relative; }
        .chantier-card:hover { transform: translateY(-5px); box-shadow: 0 20px 25px -5px rgba(0,0,0,0.05); }
        
        .card-thumb { height: 180px; position: relative; background: #f1f5f9; overflow: hidden; }
        .card-thumb img { width: 100%; height: 100%; object-fit: cover; transition: transform 0.3s; }
        .chantier-card:hover .card-thumb img { transform: scale(1.05); }
        .thumb-placeholder { width: 100%; height: 100%; display: flex; align-items: center; justify-content: center; color: #cbd5e1; }
        .status-badge { position: absolute; top: 15px; right: 15px; padding: 6px 14px; border-radius: 10px; font-size: 11px; font-weight: 800; text-transform: uppercase; letter-spacing: 0.5px; background: rgba(0,0,0,0.5); color: #fff; backdrop-filter: blur(8px); }
        .status-badge.completed { background: rgba(16, 185, 129, 0.9); }
        .status-badge.planned { background: rgba(234, 179, 8, 0.9); }
        .status-badge.active { background: rgba(14, 165, 233, 0.9); }

        .card-body { padding: 20px; display: flex; flex-direction: column; gap: 15px; }
        .card-info h3 { margin: 0 0 5px 0; font-size: 18px; font-weight: 800; color: #0f172a; }
        .card-client { font-size: 14px; font-weight: 700; color: #94a3b8; margin: 0 0 8px 0; }
        .card-loc { display: flex; align-items: center; gap: 5px; font-size: 12px; color: #64748b; font-weight: 600; }
        
        .card-footer { display: flex; justify-content: space-between; align-items: center; border-top: 1px solid #f1f5f9; padding-top: 15px; }
        .media-stats { display: flex; gap: 12px; }
        .media-stats span { display: flex; align-items: center; gap: 6px; font-size: 12px; font-weight: 700; color: #94a3b8; }
        .arrow { color: var(--admin-border); transition: transform 0.2s; }
        .chantier-card:hover .arrow { color: var(--admin-primary); transform: translateX(4px); }

        @media (max-width: 900px) {
          .chantiers-header h1 { font-size: 24px; }
          .chantiers-grid { grid-template-columns: 1fr; }
          .chantier-card { border-radius: 22px; display: flex; height: 120px; }
          .card-thumb { width: 100px; height: 100%; border-right: 1px solid #f1f5f9; }
          .status-badge { display: none; }
          .card-body { padding: 12px 18px; flex: 1; }
          .card-info h3 { font-size: 15px; }
          .card-footer { border: none; padding: 0; }
          .arrow { display: none; }
        }
        
        @keyframes fadeIn { from { opacity: 0; transform: translateY(10px); } to { opacity: 1; transform: translateY(0); } }
      `}</style>
    </div>
  );
}
